import type React from "react";
import { useCallback, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiPlus, FiTrash2, FiUpload } from "react-icons/fi";
import type { GalleryImage } from "../../lib/gallery";
import { deleteGalleryImage, uploadGalleryImage } from "../../lib/gallery";
import { easeManor } from "../../constants/constants";
import { trackEvent } from "../../lib/analytics";

type UploadingItem = { id: string; name: string; progress: "uploading" | "done" | "error"; error?: string };

type Props = {
  images: GalleryImage[];
  onChange: (next: GalleryImage[]) => void;
};

const ACCEPTED = "image/jpeg,image/png,image/webp,image/avif";
const MAX_SIZE_MB = 12;

const GalleryManager: React.FC<Props> = ({ images, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<UploadingItem[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (!list.length) return;

      const next = [...images];
      for (const file of list) {
        const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 6)}`;
        setUploading((q) => [...q, { id: key, name: file.name, progress: "uploading" }]);

        if (!file.type.startsWith("image/")) {
          setUploading((q) =>
            q.map((i) =>
              i.id === key ? { ...i, progress: "error", error: "Not an image" } : i,
            ),
          );
          continue;
        }
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
          setUploading((q) =>
            q.map((i) =>
              i.id === key
                ? { ...i, progress: "error", error: `Larger than ${MAX_SIZE_MB}MB` }
                : i,
            ),
          );
          continue;
        }

        try {
          const image = await uploadGalleryImage(file, {
            sortIndex: next.length,
          });
          trackEvent("admin_photo_uploaded", { size: file.size, type: file.type });
          next.push(image);
          onChange([...next]);
          setUploading((q) =>
            q.map((i) => (i.id === key ? { ...i, progress: "done" } : i)),
          );
          setTimeout(() => {
            setUploading((q) => q.filter((i) => i.id !== key));
          }, 1500);
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Upload failed";
          setUploading((q) =>
            q.map((i) => (i.id === key ? { ...i, progress: "error", error: msg } : i)),
          );
        }
      }
    },
    [images, onChange],
  );

  const handleDelete = useCallback(
    async (image: GalleryImage) => {
      const ok = window.confirm("Remove this photo from the gallery? This cannot be undone.");
      if (!ok) return;
      setDeletingId(image.id);
      try {
        await deleteGalleryImage(image);
        trackEvent("admin_photo_removed", { id: image.id });
        onChange(images.filter((i) => i.id !== image.id));
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Delete failed";
        window.alert(msg);
      } finally {
        setDeletingId(null);
      }
    },
    [images, onChange],
  );

  return (
    <section>
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <p className="u-label">The Salon Wall</p>
          <h3 className="mt-2 font-display text-3xl md:text-4xl text-[color:var(--claret-deep)]">
            Curate the <em>gallery</em>.
          </h3>
          <p className="mt-2 text-[color:var(--ink-soft)] max-w-xl">
            Upload photographs to display on the public gallery. Accepted: JPG,
            PNG, WebP, AVIF. Up to {MAX_SIZE_MB}MB each.
          </p>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="btn-seal"
        >
          <FiPlus size={16} />
          <span>Add photographs</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
        }}
        className={`mt-8 rounded-sm border border-dashed px-6 py-10 text-center transition-colors duration-300 ${
          dragOver
            ? "border-[color:var(--claret)] bg-[color:var(--rose-soft)]/40"
            : "border-[color:var(--gilt-hairline)] bg-[color:var(--parchment-deep)]/60"
        }`}
      >
        <FiUpload
          size={26}
          className="mx-auto text-[color:var(--gilt-deep)]"
          aria-hidden="true"
        />
        <p className="mt-3 u-label">Drop images here</p>
        <p className="mt-1 text-sm italic text-[color:var(--ink-soft)]">
          or use the button above to browse
        </p>
      </div>

      <AnimatePresence>
        {uploading.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: easeManor }}
            className="mt-6 space-y-2 overflow-hidden"
          >
            {uploading.map((u) => (
              <li
                key={u.id}
                className="flex items-center justify-between gap-4 text-sm border-b border-[color:var(--gilt-hairline)] py-2"
              >
                <span className="truncate text-[color:var(--ink-soft)]">{u.name}</span>
                <span className="u-label">
                  {u.progress === "uploading" && "Uploading…"}
                  {u.progress === "done" && "Complete"}
                  {u.progress === "error" && (u.error ?? "Error")}
                </span>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>

      <div className="mt-10 grid gap-5 [grid-template-columns:repeat(auto-fill,minmax(180px,1fr))]">
        <AnimatePresence>
          {images.map((image, idx) => (
            <motion.figure
              key={image.id}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5, ease: easeManor, delay: (idx % 8) * 0.02 }}
              className="relative group"
            >
              <div className="frame-gilt">
                <img
                  src={image.url}
                  alt={image.caption ?? "Gallery item"}
                  className="w-full h-40 object-cover"
                  loading="lazy"
                />
              </div>
              <button
                type="button"
                onClick={() => handleDelete(image)}
                disabled={deletingId === image.id}
                aria-label="Remove photo"
                className="absolute -top-3 -right-3 w-9 h-9 rounded-full grid place-items-center bg-[color:var(--claret)] text-[color:var(--parchment)] border border-[color:var(--claret-deep)] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-300 disabled:opacity-100 disabled:animate-pulse"
              >
                <FiTrash2 size={14} />
              </button>
            </motion.figure>
          ))}
        </AnimatePresence>
      </div>

      {images.length === 0 && (
        <p className="mt-10 text-center italic text-[color:var(--ink-soft)]">
          The wall is bare — upload the first photograph.
        </p>
      )}
    </section>
  );
};

export default GalleryManager;
